// One project on a person's tab (design handoff §6.2, D1-c §3.2).
//
// The whole header is the click target. Inside it: a ring, the title, the
// customer as PLAIN TEXT (not a chip — the chip is reserved for status, and
// only where a status is actually true), the movement line, and the meta.
//
// Two things about the movement line are load-bearing, and both are about NOT
// showing something:
//
//   · Under four quiet days it shows NOTHING. Long-running work legitimately
//     sits still, and a daily "no movement" badge trains the room to ignore it.
//   · With fewer than two nightly snapshots it shows nothing either, and says
//     why in the group footnote — never faked from `updated_at`, which moves
//     whenever somebody edits a summary.
//
// Both decisions are made server-side in `computeMovement`; this card renders
// `movement.line` and trusts an empty string to mean "say nothing".

import { useCallback, useEffect, useMemo, useState, type MouseEvent } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, CircularProgress, Menu, MenuItem, Tooltip } from "@mui/material";
import { useSnackbar } from "notistack";
import { brainTokens } from "../design/brainTheme";
import {
  getProjectJiraIssues,
  listProjectActions,
  transitionProject,
  PROJECT_STATUS_LABELS,
  type ProjectAction,
  type ProjectJiraIssuesResponse,
  type ProjectStatus,
} from "../lib/brain-api";
import type { ParkedPin, PersonProjectCard as CardData } from "../lib/api";
import { type ActionRegisterPerson } from "./ActionRegisterTable";
import { ProjectRegister, type RegisterPull } from "./ProjectRegister";
import { Icon } from "./icons";
import { PersonAvatar } from "./PersonAvatar";
import { ProgressRing } from "./ProgressRing";
import { projectStatusMeta } from "./ProjectsTable";
import { Segmented } from "./Segmented";
import { SegmentedMulti } from "./SegmentedMulti";
import { countTheirs, rowIsTheirs } from "./personRows";
import type { RegisterRow } from "./registerRows";
import {
  buildRegisterRows,
  openStatusLabels,
  statusFilterKey,
  statusFilterLabel,
} from "./registerRows";

const c = brainTokens.color;

/**
 * Same strip as the project record. `draft` is deliberately NOT offered (Isaac
 * 2026-08-13) — it stays a valid stored value so projects already sitting in it
 * still render, it just isn't somewhere you can move a project to. `archived` is
 * a record-menu action, not a thing you reach for mid-standup.
 */
const STATUS_CHOICES: ProjectStatus[] = [
  "planning", "in_progress", "on_hold", "with_client", "done",
];

const MOVEMENT_DOT: Record<string, string> = {
  success: c.successDot,
  warning: c.warnDot,
  danger: c.dangerText,
  none: c.border,
};
const MOVEMENT_TEXT: Record<string, string> = {
  success: c.successText,
  warning: c.warnText,
  danger: c.dangerText,
  none: c.muted,
};

/** `2 actions done · 1 Jira moved · 1 new` — or nothing at all, on purpose. */
export function MovementLine({ line, tone }: { line: string; tone: string }) {
  if (!line) return null;
  return (
    <Box
      component="span"
      data-testid="movement-line"
      sx={{ display: "inline-flex", alignItems: "center", gap: 0.9, whiteSpace: "nowrap" }}
    >
      <Box
        aria-hidden
        sx={{ width: 8, height: 8, borderRadius: "50%", flex: "none", background: MOVEMENT_DOT[tone] ?? c.border }}
      />
      <Box component="span" sx={{ fontSize: 14, fontWeight: 500, color: MOVEMENT_TEXT[tone] ?? c.muted }}>
        {line}
      </Box>
    </Box>
  );
}

/**
 * Parked work, shown on the project it belongs to. Parking is only honest if
 * the cost stays visible: the row left Today but it is still open here, and the
 * since-date says how long that has been true.
 */
function ParkedStrip({ pins }: { pins: ParkedPin[] }) {
  if (!pins.length) return null;
  return (
    <Box
      data-testid="parked-strip"
      sx={{
        m: "0 12px 10px",
        p: "10px 12px",
        border: `1px solid ${c.border}`,
        background: c.neutralPillBg,
        borderRadius: `${brainTokens.radius.card}px`,
      }}
    >
      <Box
        component="span"
        sx={{
          fontFamily: brainTokens.font.mono,
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.11em",
          color: c.muted,
        }}
      >
        PARKED · OFF TODAY, STILL OPEN
      </Box>
      {pins.map((p) => (
        <Box key={p.focusId} sx={{ display: "flex", alignItems: "baseline", gap: 1.25, mt: 0.85 }}>
          <Box
            component="span"
            sx={{
              fontFamily: brainTokens.font.mono,
              fontSize: 11,
              fontWeight: 700,
              color: c.muted,
              whiteSpace: "nowrap",
              flex: "none",
            }}
          >
            {p.parkedDays}d
          </Box>
          <Box sx={{ fontSize: 13, color: c.ink3, minWidth: 0, flex: 1 }}>{p.text}</Box>
          <Box
            component="span"
            sx={{ fontFamily: brainTokens.font.mono, fontSize: 11, color: c.faint, whiteSpace: "nowrap" }}
          >
            since {p.parkedSince}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

export function PersonProjectCard({
  card,
  personName,
  personKeys,
  jiraAccountId,
  parked = [],
  nzDate,
  people = [],
  canEdit,
  pull,
  overrideActions,
  defaultExpanded = false,
}: {
  card: CardData;
  personName: string;
  /** Slug/name keys used to decide which rows are theirs. */
  personKeys: string[];
  jiraAccountId?: string;
  /** This project's parked pins, so the cost of parking stays visible. */
  parked?: ParkedPin[];
  nzDate: string;
  people?: ActionRegisterPerson[];
  canEdit: boolean;
  pull: RegisterPull;
  /**
   * Write-through from the Today rail. A tick closes the register row on the
   * server; this lets the owning tab replay that onto the card's own copy so
   * the row reads `Done` immediately — a pin is not a copy (handoff §6.3).
   */
  overrideActions?: (actions: ProjectAction[] | null) => ProjectAction[] | null;
  defaultExpanded?: boolean;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(defaultExpanded);
  const [loading, setLoading] = useState(false);
  const [rawActions, setActions] = useState<ProjectAction[] | null>(null);
  const actions = useMemo(
    () => (overrideActions ? overrideActions(rawActions) : rawActions),
    [overrideActions, rawActions],
  );
  const [jira, setJira] = useState<ProjectJiraIssuesResponse | null>(null);
  // Always open pre-filtered to this person's rows — owner included. Toggle
  // "All rows" when the room needs the whole register.
  const [showAll, setShowAll] = useState(false);
  // Open / swimlane statuses / Done / All, multi-select and OR-ed. Status chips
  // use `status:` keys; an empty selection means "All".
  const [statusFilters, setStatusFilters] = useState<string[]>(["open"]);

  // Server decides whose figure this is; an older deployment sends neither
  // field, in which case the ring still means what it always did.
  const ringTitle = useMemo(() => {
    const doneMine = card.counts.yoursDone;
    if (card.pctScope === "project" || doneMine == null) {
      return `${card.pct}% of this project is done — ${personName} holds no rows here`;
    }
    return `${personName}: ${doneMine} of ${doneMine + card.counts.yours} done`;
  }, [card.pct, card.pctScope, card.counts.yours, card.counts.yoursDone, personName]);

  const loadRegister = useCallback(async () => {
    if (rawActions !== null) return;
    setLoading(true);
    const [nextActions, nextJira] = await Promise.all([
      listProjectActions({ project: card.projectId }).catch((e) => {
        enqueueSnackbar(e instanceof Error ? e.message : "Failed to load actions.", { variant: "error" });
        return [] as ProjectAction[];
      }),
      getProjectJiraIssues(card.projectId).catch(() => null),
    ]);
    setActions(nextActions);
    setJira(nextJira);
    setLoading(false);
  }, [rawActions, card.projectId, enqueueSnackbar]);

  useEffect(() => {
    if (open) void loadRegister();
  }, [open, loadRegister]);

  const allRows = useMemo(
    () => (actions === null ? [] : buildRegisterRows({ issues: jira?.issues, actions })),
    [actions, jira],
  );
  // Support desk JQL keeps ~14d of Done for "done this week" meta — don't count
  // those in the Lewis (N) filter chip or the person will look flooded.
  const countRows = useMemo(
    () => (card.kind === "support" ? allRows.filter((r) => !r.done) : allRows),
    [allRows, card.kind],
  );
  const mineCount = useMemo(
    () => countTheirs(countRows, personKeys, jiraAccountId),
    [countRows, personKeys, jiraAccountId],
  );
  const rowFilter = useMemo(
    () => (showAll ? undefined : (row: RegisterRow) => rowIsTheirs(row, personKeys, jiraAccountId)),
    [showAll, personKeys, jiraAccountId],
  );
  const personScopedRows = useMemo(
    () => (rowFilter ? allRows.filter(rowFilter) : allRows),
    [allRows, rowFilter],
  );
  const statusChipLabels = useMemo(() => openStatusLabels(personScopedRows), [personScopedRows]);
  const statusFilterOptions = useMemo(
    () => [
      { key: "open", label: "Open" },
      ...statusChipLabels.map((label) => ({ key: statusFilterKey(label), label })),
      { key: "done", label: "Done" },
      { key: "all", label: "All" },
    ],
    [statusChipLabels],
  );

  // Drop swimlane chips that no longer exist (the status emptied out). If that
  // clears the whole selection, fall back to Open rather than silently widening
  // to everything.
  useEffect(() => {
    setStatusFilters((cur) => {
      const kept = cur.filter((f) => {
        const label = statusFilterLabel(f);
        return label === null || statusChipLabels.includes(label);
      });
      if (kept.length === cur.length) return cur;
      return kept.length ? kept : ["open"];
    });
  }, [statusChipLabels]);

  const bandOrder = useMemo(
    () => (jira?.scopes?.length ? jira.scopes.map((s) => s.name) : card.bands),
    [jira, card.bands],
  );

  // Project status, surfaced on the CLOSED card and changeable from here — the
  // standup is where the room finds out a project has moved, so making them open
  // the record to say so is the step that never happens. Held locally so the
  // change shows instantly; the board poll re-supplies the server's copy.
  const [status, setStatus] = useState<string | undefined>(card.status);
  useEffect(() => setStatus(card.status), [card.status]);
  const [statusAnchor, setStatusAnchor] = useState<HTMLElement | null>(null);
  const [statusBusy, setStatusBusy] = useState(false);
  const statusMeta = projectStatusMeta(card.blocked ? "blocked" : (status ?? ""));

  const changeStatus = useCallback(
    async (to: ProjectStatus) => {
      setStatusAnchor(null);
      if (to === status) return;
      const prev = status;
      setStatus(to);
      setStatusBusy(true);
      try {
        await transitionProject(card.projectId, to);
        enqueueSnackbar(`${card.name} → ${PROJECT_STATUS_LABELS[to]}`, { variant: "success" });
      } catch (e) {
        setStatus(prev);
        enqueueSnackbar(e instanceof Error ? e.message : "Could not change the status.", {
          variant: "error",
        });
      } finally {
        setStatusBusy(false);
      }
    },
    [status, card.projectId, card.name, enqueueSnackbar],
  );
  const ownerName = card.owner?.trim() ?? "";
  const isInternal = !card.customer || card.customer === "aeronet";
  // Tolerate a route that predates `assignees` — an older bundle must degrade to
  // "no collaborator avatars", never take the whole tab down with it.
  const assignees = card.assignees ?? [];

  return (
    <Box
      data-testid="person-project-card"
      data-project={card.projectId}
      sx={{
        background: c.white,
        border: `1px solid ${open ? c.hoverBorder : c.border}`,
        borderRadius: `${brainTokens.radius.card}px`,
        overflow: "hidden",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "stretch" }}>
        {/* A clickable REGION, not a <button>: the status chip inside it is
            itself a button, and a button inside a button is invalid HTML that
            browsers handle inconsistently. The chevron below is the real,
            focusable toggle, so keyboard and screen-reader users keep a proper
            control while the whole header stays clickable for the mouse. */}
        <Box
          onClick={() => setOpen((v) => !v)}
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "auto 1fr", md: "auto 1fr auto" },
            gap: { xs: 1.5, md: 2.5 },
            alignItems: "center",
            flex: 1,
            minWidth: 0,
            p: "18px 20px",
            textAlign: "left",
            cursor: "pointer",
            userSelect: "none",
            "&:hover": { background: c.canvas },
          }}
        >
          {/* The ring is this person's slice, so it has to SAY so on hover —
              an unlabelled 40% next to "152 done" reads as a contradiction. */}
          <Tooltip title={ringTitle}>
            <Box component="span" sx={{ display: "inline-flex", flex: "none" }}>
              <ProgressRing pct={card.pct} size={44} stroke={4} />
            </Box>
          </Tooltip>

          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: "flex", gap: 0.6, alignItems: "center", flexWrap: "wrap", mb: 0.5 }}>
              <Box
                component="span"
                sx={{
                  fontSize: 11.5,
                  fontWeight: 650,
                  px: 1,
                  py: 0.2,
                  borderRadius: `${brainTokens.radius.pill}px`,
                  background: isInternal ? c.neutralPillBg : c.tealTint,
                  color: isInternal ? c.muted : c.tealInk,
                }}
              >
                {isInternal ? "Internal" : "Customer"}
                {card.customer && !isInternal ? ` · ${card.customer}` : ""}
              </Box>

              {/* Status sits with the kind chip, above the title — it reads as a
                  property OF the project, which is what it is. */}
              <Box
                component="button"
                type="button"
                disabled={statusBusy}
                aria-haspopup="menu"
                title={`Status: ${statusMeta.word} — click to change`}
                onClick={(e: MouseEvent<HTMLElement>) => {
                  e.stopPropagation();
                  setStatusAnchor(e.currentTarget);
                }}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "5px",
                  border: `1px solid ${statusMeta.tone === "teal" ? c.tealTintBorder : c.border}`,
                  background: c.white,
                  borderRadius: `${brainTokens.radius.pill}px`,
                  px: 1,
                  py: 0.2,
                  fontFamily: brainTokens.font.sans,
                  fontSize: 11.5,
                  fontWeight: 650,
                  lineHeight: 1.45,
                  color: statusMeta.color,
                  cursor: statusBusy ? "default" : "pointer",
                  opacity: statusBusy ? 0.5 : 1,
                  "&:hover": { background: c.canvas, borderColor: c.hoverBorder },
                }}
              >
                <Box
                  aria-hidden
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    flex: "none",
                    background: statusMeta.color,
                  }}
                />
                {statusMeta.word}
                <Box aria-hidden sx={{ fontSize: 9, color: c.faint, ml: "1px" }}>
                  ▾
                </Box>
              </Box>
            </Box>
            <Box sx={{ fontSize: 17, fontWeight: 650, color: c.ink, mb: 0.35, lineHeight: 1.3 }}>
              {card.name}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <MovementLine line={card.movement.line} tone={card.movement.tone} />
              {/* Counts come from the route, so a CLOSED card is already honest. */}
              <Box component="span" sx={{ fontSize: 13, color: c.muted, whiteSpace: "nowrap" }}>
                <Box component="b" sx={{ color: c.ink, fontWeight: 650 }}>{card.counts.open}</Box> open ·{" "}
                <Box component="b" sx={{ color: c.ink, fontWeight: 650 }}>{card.counts.yours}</Box> theirs
                {card.counts.blocked > 0 && ` · ${card.counts.blocked} blocked`}
                {card.counts.done > 0 && ` · ${card.counts.done} done`}
              </Box>
              {card.bands.length > 1 && (
                <Box
                  component="span"
                  sx={{ fontFamily: brainTokens.font.mono, fontSize: 12, color: c.faint, whiteSpace: "nowrap" }}
                >
                  {card.bands.length} workstreams
                </Box>
              )}
            </Box>
          </Box>

          {/* Owner + collaborators, right-aligned — same read as the Projects tab. */}
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 0.75,
            }}
          >
            {ownerName ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }} title={`Owner — ${ownerName}`}>
                <Box sx={{ fontSize: 12.5, fontWeight: 650, color: c.ink, whiteSpace: "nowrap" }}>
                  {ownerName}
                </Box>
                <PersonAvatar name={ownerName} size={30} />
              </Box>
            ) : (
              <Box sx={{ fontSize: 12, color: c.faint }}>No owner</Box>
            )}
            {assignees.length > 0 && (
              <Box sx={{ display: "flex", gap: 0.4, flexWrap: "wrap", justifyContent: "flex-end", maxWidth: 160 }}>
                {assignees.slice(0, 6).map((who) => (
                  <Box key={who} title={who}>
                    <PersonAvatar name={who} size={20} />
                  </Box>
                ))}
                {assignees.length > 6 && (
                  <Box sx={{ fontSize: 11, color: c.muted, alignSelf: "center" }}>
                    +{assignees.length - 6}
                  </Box>
                )}
              </Box>
            )}
            {/* The real toggle — the header region around it is mouse sugar. */}
            <Box
              component="button"
              type="button"
              aria-expanded={open}
              aria-label={`${open ? "Collapse" : "Expand"} the ${card.name} register`}
              onClick={(e: MouseEvent) => {
                e.stopPropagation();
                setOpen((v) => !v);
              }}
              sx={{
                border: "none",
                background: "none",
                p: 0,
                cursor: "pointer",
                fontSize: 12,
                lineHeight: 1,
                color: c.teal,
                transform: open ? "rotate(180deg)" : "none",
                transition: "transform 0.15s ease",
                "@media (prefers-reduced-motion: reduce)": { transition: "none" },
              }}
            >
              ⌄
            </Box>
          </Box>
        </Box>

        {/* Straight to the project record — the card is a summary, not the home. */}
        <Box
          component={RouterLink}
          to={`/projects/${encodeURIComponent(card.projectId)}`}
          // New tab: the standup is a live meeting surface — navigating away
          // mid-walkthrough loses the room's place in the card list.
          target="_blank"
          rel="noopener"
          title={`Open ${card.name} in a new tab`}
          aria-label={`Open the ${card.name} project record in a new tab`}
          onClick={(e: MouseEvent) => e.stopPropagation()}
          sx={{
            display: "flex",
            alignItems: "flex-start",
            pt: "20px",
            pr: "14px",
            pl: "6px",
            flex: "none",
            lineHeight: 0,
            color: c.muted2,
            "&:hover": { color: c.teal },
          }}
        >
          <Icon name="external-link" size={15} />
        </Box>
      </Box>

      <Menu
        anchorEl={statusAnchor}
        open={!!statusAnchor}
        onClose={() => setStatusAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        {STATUS_CHOICES.map((s) => (
          <MenuItem
            key={s}
            selected={s === status}
            onClick={() => void changeStatus(s)}
            sx={{ fontSize: 13 }}
          >
            {PROJECT_STATUS_LABELS[s]}
          </MenuItem>
        ))}
      </Menu>

      {open && (
        <Box sx={{ borderTop: `1px solid ${c.borderLight2}`, background: c.panelBg, pt: 1.5, pb: 1.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.75,
              px: "12px",
              pb: 1.25,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Segmented
                value={showAll ? "all" : "mine"}
                onChange={(v) => setShowAll(v === "all")}
                options={[
                  { key: "mine", label: `${personName} (${mineCount})` },
                  { key: "all", label: "All rows" },
                ]}
              />
              <SegmentedMulti
                value={statusFilters}
                onChange={setStatusFilters}
                options={statusFilterOptions}
                clearKey="all"
              />
            </Box>
          </Box>

          <ParkedStrip pins={parked} />

          {loading || actions === null ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={22} />
            </Box>
          ) : (
            <ProjectRegister
              projectId={card.projectId}
              projectName={card.name}
              issues={jira?.issues}
              actions={actions}
              bandOrder={bandOrder}
              truncated={jira?.truncated}
              nzDate={nzDate}
              people={people}
              canQuickAdd={canEdit}
              canEdit={canEdit}
              onActionsChange={setActions}
              onIssuesChange={(next) =>
                setJira((cur) => (cur ? { ...cur, issues: next } : cur))
              }
              pull={pull}
              rowFilter={rowFilter}
              doneFilter={statusFilters}
              showOrganization={card.kind === "support"}
              emptyText={(() => {
                const swimlanes = statusFilters
                  .map(statusFilterLabel)
                  .filter((l): l is string => l !== null);
                if (swimlanes.length) {
                  const named = swimlanes.map((s) => `"${s}"`).join(" / ");
                  return showAll
                    ? `No tickets in ${named}.`
                    : `No tickets in ${named} assigned to ${personName}.`;
                }
                if (statusFilters.length === 1 && statusFilters[0] === "done") {
                  return showAll
                    ? "No done or dismissed work on this project yet."
                    : `No done or dismissed work assigned to ${personName}.`;
                }
                return showAll
                  ? "No issues or actions on this project yet."
                  : `Nothing on this project is assigned to ${personName}. Switch to All rows to see the whole register.`;
              })()}
            />
          )}
        </Box>
      )}
    </Box>
  );
}
